import { forwardRef } from "react";
import { Receipt, Building2, User, MapPin, Truck } from "@semo/icons";
import type { ChatContractCompleteCardProps } from "../types";
import { cn, ReceiptButton, Button } from "@semo/shared-ui";
import {
  ContractSettlementBadgeChip,
  resolveContractSettlementBadge,
} from "./contract-settlement-status";
import { ContractInfoDtoPaymentMethod } from "../types";
import { MessageCardWrapper } from "../layout/MessageCardWrapper";

/**
 * ContractCompleteCard - 계약 체결 완료 카드 (거래명세서 양식)
 *
 * 전자 계약이 정상적으로 체결되었음을 알리는 카드입니다.
 * 실제 거래명세서 양식으로 디자인되어 공급자와 구매자 정보, 상세 품목 리스트를 보여줍니다.
 */
export const ContractCompleteCard = forwardRef<
  HTMLDivElement,
  ChatContractCompleteCardProps
>(({
  message,
  contractId,
  sender,
  paymentMethod,
  onViewContract,
  paymentChannel = null,
  settlesOnCmarket = true,
  isPaid = false,
  taxInvoiceStatus = null,
  onOpenCmarketContracts,
}, ref) => {
  const { createdAt, contractInfo } = message;
  const tid = contractInfo?.tid || contractInfo?.contractId;
  const contractNo =
    contractInfo?.contractId?.split("-")[0]?.toUpperCase() || "-";

  // Calculate VAT (10%) and Net Amount
  const total = Number(contractInfo?.totalAmount || 0);
  const netAmount = Math.round(total / 1.1);
  const vatAmount = total - netAmount;

  // Show receipt only for card payments (CARD) or when paymentMethod is not specified
  const shouldShowReceipt =
    tid &&
    paymentMethod !== ContractInfoDtoPaymentMethod.CASH &&
    paymentMethod !== ContractInfoDtoPaymentMethod.DIRECT;

  // 대금 정리 섹션의 배지 — 현금은 계산서 발행 상태, 카드는 결제 완료 여부.
  const settlementBadge = resolveContractSettlementBadge({
    paymentChannel,
    isPaid,
    taxInvoiceStatus,
  });

  /*
    안내 문구는 두지 않는다(2026-08-28).

    씨마켓 결제창·«나의 계약 관리» 를 설명하던 자리인데, 세모 안심결제 계약에도 같은 말이
    붙어 대금이 어디로 가는지를 거꾸로 알려 줬다. 상태는 배지가, 다음에 할 일은 버튼이
    이미 말한다 — 그 사이에 낀 설명은 맞을 때조차 읽히지 않았고 틀릴 때는 해로웠다.
  */

  // 이미 요청·발행된 뒤에도 버튼은 살아 있다(재요청이 정상 경로). 대신 말이 바뀐다 —
  // 발행이 끝났는데 [계산서 발급요청] 이 그대로 있으면 아직 안 된 줄로 읽힌다.
  const settlementActionLabel =
    paymentChannel === "card"
      ? "카드결제하기"
      : taxInvoiceStatus === "ISSUED"
        ? "계산서 확인하기"
        : taxInvoiceStatus === "REQUESTED"
          ? "계산서 발급 현황 보기"
          : "계산서 발급요청";

  return (
    <MessageCardWrapper
      ref={ref}
      data-slot="chat-contract-complete-card"
      size="document"
      className={cn(
        "bg-white ",
        "border rounded-xl p-4 sm:p-6 shadow-sm relative overflow-hidden",
        sender === "me" ? "border-primary/30 " : "border-primary/25 ",
      )}
    >
      {/* Header: Title & Meta */}
      <div className="flex flex-col items-center mb-6 border-b-2 border-slate-900 pb-4">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-[0.2em] text-slate-900 mb-1">
          거 래 명 세 서
        </h2>
        <div className="flex gap-4 text-2xs sm:text-xs text-slate-500">
          <span>일자: {new Date(createdAt).toLocaleDateString("ko-KR")}</span>
          <span>번호: {contractNo}</span>
        </div>
      </div>

      {/* Info Stack: Supplier & Buyer (Vertical Layout, Multi-line Content) */}
      <div className="flex flex-col gap-4 mb-6">
        {/* Supplier (공급자) */}
        <div className="flex flex-col gap-1.5">
          <h3 className="text-xs sm:text-sm font-semibold text-slate-900 flex items-center gap-1.5 ml-1">
            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
            공급사
          </h3>
          <div className="bg-slate-50 rounded-lg border border-slate-100 p-3 sm:p-3.5 text-2xs sm:text-xs text-slate-700">
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
                <span className="font-semibold text-slate-900">
                  {contractInfo?.supplierCompanyName || "-"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
                <span>
                  {contractInfo?.supplierCeoName || "-"}
                  <span className="text-slate-400 ml-1">
                    ({contractInfo?.supplierPhone || "-"})
                  </span>
                </span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0 mt-0.5" />
                <span className="break-keep leading-relaxed">
                  {contractInfo?.supplierAddress || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Buyer (공급받는자) */}
        <div className="flex flex-col gap-1.5">
          <h3 className="text-xs sm:text-sm font-semibold text-slate-900 flex items-center gap-1.5 ml-1">
            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
            구매자
          </h3>
          <div className="bg-slate-50 rounded-lg border border-slate-100 p-3 sm:p-3.5 text-2xs sm:text-xs text-slate-700">
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
                <span className="font-semibold text-slate-900">
                  {contractInfo?.buyerAgencyName || "-"}
                </span>
              </div>
              {/*
                담당자 이름 + **기관 대표번호**. 종전에는 괄호 안이 담당자 개인 휴대폰이었다
                (c-market#5762) — 발주기관 거래에서 개인 번호를 문서에 남기지 않는다.
                기관이 없는 개인 구매자는 대표번호가 없어 이름만 남는다.
              */}
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
                <span>
                  {contractInfo?.buyerManagerName || "-"}
                  {contractInfo?.buyerOrganizationPhone && (
                    <span className="text-slate-400 ml-1">
                      ({contractInfo.buyerOrganizationPhone})
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Truck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0 mt-0.5" />
                <span className="break-keep leading-relaxed font-semibold text-primary-text">
                  {contractInfo?.deliveryAddress ||
                    contractInfo?.buyerAddress ||
                    "계약서 상세 참조"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-6 border rounded-lg overflow-hidden border-slate-200">
        <table className="w-full text-left text-xs table-fixed">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr>
              <th className="px-1 sm:px-3 py-2 font-semibold text-slate-700 w-8 sm:w-10 text-center">
                No
              </th>
              <th className="px-2 sm:px-3 py-2 font-semibold text-slate-700">
                품목 / 규격
              </th>
              <th className="px-1 sm:px-3 py-2 font-semibold text-slate-700 text-center w-10 sm:w-14">
                수량
              </th>
              <th className="px-1 sm:px-3 py-2 font-semibold text-slate-700 text-right w-16 sm:w-20">
                단가
              </th>
              <th className="px-1 sm:px-3 py-2 font-semibold text-slate-700 text-right w-16 sm:w-24">
                금액
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {contractInfo?.items && contractInfo.items.length > 0 ? (
              contractInfo.items.map((item: any, idx: number) => (
                <tr key={idx} className="hover:bg-slate-50/50">
                  <td className="px-1 sm:px-3 py-2 text-slate-400 text-center">
                    {idx + 1}
                  </td>
                  <td className="px-2 sm:px-3 py-2 overflow-hidden">
                    <div
                      className="font-medium text-slate-900 line-clamp-2 break-all sm:break-keep"
                      title={item.name}
                    >
                      {item.name}
                    </div>
                    {item.spec && (
                      <div
                        className="text-2xs text-slate-500 mt-0.5 line-clamp-1 truncate"
                        title={item.spec}
                      >
                        {item.spec}
                      </div>
                    )}
                  </td>
                  <td className="px-1 sm:px-3 py-2 text-slate-900 text-center truncate">
                    {item.quantity}
                    {item.unit || ""}
                  </td>
                  <td className="px-1 sm:px-3 py-2 text-slate-900 text-right truncate">
                    {item.unitPrice.toLocaleString()}
                  </td>
                  <td className="px-1 sm:px-3 py-2 font-semibold text-slate-900 text-right truncate">
                    {item.amount.toLocaleString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-8 text-center text-slate-400"
                >
                  상세 품목은 계약서를 확인해주세요.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary (VAT / Total) */}
      <div className="flex flex-col items-end gap-1 mb-8">
        <div className="flex justify-between w-full max-w-[200px] text-xs text-slate-500">
          <span>공급가액</span>
          <span>{netAmount.toLocaleString()}원</span>
        </div>
        <div className="flex justify-between w-full max-w-[200px] text-xs text-slate-500 border-b border-slate-100 pb-1">
          <span>부가가치세</span>
          <span>{vatAmount.toLocaleString()}원</span>
        </div>
        <div className="flex justify-between w-full max-w-[200px] mt-1">
          <span className="text-sm font-semibold text-slate-900">
            합계 금액
          </span>
          <span className="text-base font-semibold text-primary-text">
            {total.toLocaleString()}원
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={() => {
            onViewContract(contractId);
          }}
          variant="default"
          className="flex-1 py-2.5 h-auto text-sm font-semibold rounded-lg shadow-sm shadow-primary/20"
        >
          계약서 상세보기
        </Button>
        {shouldShowReceipt && (
          <ReceiptButton
            tid={tid}
            variant="outline"
            className={cn(
              "flex-1 py-2.5 h-auto",
              "bg-white ",
              "border-slate-200 ",
              "text-slate-600 ",
              "text-sm font-semibold rounded-lg",
              "transition-colors",
            )}
          >
            결제 영수증
          </ReceiptButton>
        )}
      </div>

      {/*
        대금 정리 섹션 — 계약 시 고른 채널로 갈린다.

        세모는 이 대금을 받지 않는다. 현금이면 공급기업이 발행할 계산서를 요청하고, 카드면
        씨마켓 결제창으로 결제한다. 둘 다 실제 처리는 씨마켓에서 일어나고 이 버튼은 거기로
        데려가는 일만 한다 — 공급 완료 여부와 무관하게 **언제 눌러도 된다.**

        **상태 표시와 버튼은 다른 게이트를 쓴다.** 버튼은 씨마켓으로 데려가는 일이라
        발주기관 임베드에서만 의미가 있지만(`onOpenCmarketContracts` 주입 여부가 곧 게이트),
        "계산서가 발행됐는가" 는 공급사도 알아야 한다. 종전에는 둘을 한 조건으로 묶어
        공급사 화면에서 섹션이 통째로 사라졌다 — 그래서 현금 계약의 발행 완료가 어느 쪽에도
        보이지 않았다. 지금은 채널만 알면 상태를 그리고, 버튼만 따로 잠근다.

        완료 상태는 버튼을 죽이지 않고 배지로 표기한다 — 죽은 버튼은 "왜 안 눌리지"를 만든다.
        특히 현금은 발행취소 후 재요청이 정상 경로라 절대 잠그면 안 된다.
      */}
      {/*
        **세모 안심결제 계약에는 이 칸이 없다.** 아래 버튼은 전부 씨마켓으로 데려가는
        일인데, 그쪽 계약은 대금을 세모가 이미 받았고 계산서도 씨마켓에서 끊지 않는다.
        「지급 수단이 정해졌는가」만 보던 종전 조건은 두 길을 가르지 못했다 — 카드·현금은
        양쪽 다 쓰는 수단이라서다. 판정은 서버가 하고(`settlesOnCmarket`) 화면은 따른다.
      */}
      {paymentChannel && settlesOnCmarket && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-semibold text-slate-900">
                {paymentChannel === "cash" ? "세금계산서" : "카드 결제"}
              </p>
              {settlementBadge && (
                <ContractSettlementBadgeChip badge={settlementBadge} />
              )}
            </div>
            {onOpenCmarketContracts && (
              <Button
                type="button"
                onClick={() =>
                  onOpenCmarketContracts(contractId, paymentChannel)
                }
                className="w-full py-2.5 h-auto text-sm font-semibold rounded-lg"
                variant={
                  paymentChannel === "card" && isPaid ? "outline" : "default"
                }
              >
                {settlementActionLabel}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Footer Stamp/Note */}
      <div className="mt-4 pt-4 border-t border-dashed border-slate-200 flex justify-between items-center opacity-60">
        <span className="text-2xs text-slate-400">
          위 금액을 정히 수령(청구)함
        </span>
        <div className="flex items-center gap-2">
          <span className="text-2xs font-semibold text-slate-900">
            SEMO
          </span>
          <Receipt className="w-4 h-4 text-primary-text" />
        </div>
      </div>
    </MessageCardWrapper>
  );
});

ContractCompleteCard.displayName = "ChatContractCompleteCard";
